import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback } from "react";
import { BillLineItem } from "@common/types/bill";
import { useUpdateBillLineItem } from "@api/bill.api";
import {
    AccrualAccountCell,
    UpdateLineItemAccountParams,
} from "@components/lineItems/AccrualAccountCell";
import { LineItemFallbackAccountType } from "@common/helpers/accounting";
import { isBillLineOwnedBySchedule } from "@components/bills/schedules/billScheduleCellState";
import {
    getLedgerIdFromColumnId,
    getLedgerNameFromColumnId,
} from "@lib/ledgerViewColumns";

export const AccrualLedgerCell = memo(
    ({
        row,
        column,
    }: CellContext<ViewTableFeatures, BillLineItem, unknown>) => {
        const updateBillLineItem = useUpdateBillLineItem();
        const lineItem = row.original;
        const ledgerId = getLedgerIdFromColumnId(column.id);
        const ledgerName = getLedgerNameFromColumnId(column.id);

        const onUpdateLineItemAccount = useCallback(
            ({
                workspaceId,
                lineItemId,
                accountOverrides,
                accountId,
            }: UpdateLineItemAccountParams) =>
                updateBillLineItem.mutateAsync({
                    workspaceId,
                    lineItemId,
                    accountOverrides,
                    accountId,
                }),
            [updateBillLineItem],
        );

        return (
            <AccrualAccountCell
                lineItemId={lineItem.id}
                lineItemAccountId={lineItem.accountId}
                accountOverrides={lineItem.accountOverrides}
                ledgerId={ledgerId}
                ledgerName={ledgerName}
                fallbackMode={LineItemFallbackAccountType.billExpense}
                entityId={lineItem.bill?.entityId}
                isLockedBySchedule={isBillLineOwnedBySchedule(lineItem)}
                onUpdateLineItemAccount={onUpdateLineItemAccount}
            />
        );
    },
);
